import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

const Settings = () => {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'reception'
  });

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call Supabase Edge Function to get all users (requires admin privileges)
      const { data, error: fnError } = await supabase.functions.invoke('admin-get-users');
      
      if (fnError) {
        console.error('Error loading users:', fnError);
        throw new Error(fnError.message || 'Kullanıcılar yüklenemedi');
      }
      
      console.log('Users loaded from Supabase:', data);
      setUsers(data.users || []);
      
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Kullanıcılar yüklenirken hata oluştu: ' + err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      console.log('Changing role for user:', userId, 'to:', newRole);
      setError(null);
      
      // Call Supabase Edge Function to update user role
      const { data, error: fnError } = await supabase.functions.invoke('admin-update-user-role', {
        body: { userId, role: newRole }
      });
      
      if (fnError) {
        throw new Error(fnError.message || 'Rol güncellenemedi');
      }
      
      console.log('Role changed successfully:', data);
      
      // Kendi rolünü değiştiriyorsa sayfa yenile
      if (userId === user.id) {
        window.location.reload();
      } else {
        // Diğer kullanıcıların rollerini güncelle
        await loadUsers();
      }
      
    } catch (err) {
      console.error('Error changing role:', err);
      setError('Rol değiştirilirken hata oluştu: ' + err.message);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      setLoading(true);
      
      console.log('Creating user with data:', newUser);
      
      // Call Supabase Edge Function to create user
      const { data, error: fnError } = await supabase.functions.invoke('admin-create-user', {
        body: {
          email: newUser.email,
          password: newUser.password,
          role: newUser.role
        }
      });
      
      if (fnError) {
        throw new Error(fnError.message || 'Kullanıcı oluşturulamadı');
      }
      
      console.log('User created successfully:', data);
      
      // Reset form and close modal
      setNewUser({ email: '', password: '', role: 'reception' });
      setShowAddUserModal(false);
      
      // Kullanıcı listesini yenile
      await loadUsers();
      
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Kullanıcı eklenirken hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`${userEmail} kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      try {
        setError(null);
        setLoading(true);

        console.log('🗑️ Attempting to delete user:', userId);

        // Call Supabase Edge Function to delete user
        const { data, error: fnError } = await supabase.functions.invoke('admin-delete-user', {
          body: { userId }
        });

        if (fnError) {
          console.error('Function error:', fnError);
          throw new Error(fnError.message || 'Kullanıcı silinemedi');
        }

        // Check if the response indicates success
        if (data && data.success === false) {
          console.error('Function returned error:', data.error);
          throw new Error(data.error?.message || data.error || 'Kullanıcı silinemedi');
        }

        console.log('User deleted successfully:', data);

        // Kullanıcı listesini yenile
        await loadUsers();

      } catch (err) {
        console.error('Error deleting user:', err);
        const errorMessage = err.message || 'Kullanıcı silinirken beklenmeyen bir hata oluştu';
        setError(`Silme işlemi başarısız: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'warehouse':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'reception':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin':
        return 'Yönetici';
      case 'warehouse':
        return 'Depo';
      case 'reception':
        return 'Resepsiyon';
      default:
        return role;
    }
  };

  if (!isAdmin()) {
    return (
      <div className="mobile-container py-6">
        <div className="mobile-card">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Yetkisiz Erişim</h3>
            <p className="text-gray-500">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-container py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="mobile-text-2xl font-bold text-gray-900 mb-2">Kullanıcı Yönetimi</h1>
        <p className="text-gray-600">Sistem kullanıcılarını yönetin ve rollerini düzenleyin</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddUserModal(true)}
          className="mobile-button btn-primary inline-flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Yeni Kullanıcı Ekle
        </button>
      </div>

      {/* Users List */}
      <div className="mobile-card">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Kullanıcılar</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Sistemde kayıtlı tüm kullanıcılar ve rolleri
          </p>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Yükleniyor...
            </div>
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Kullanıcı bulunamadı</h3>
            <p className="mt-1 text-sm text-gray-500">Henüz hiç kullanıcı eklenmemiş.</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {users.map((userData) => (
                <li key={userData.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {userData.auth?.users?.email || 'Bilinmeyen Email'}
                        </div>
                        <div className="text-sm text-gray-500">
                          Kayıt: {new Date(userData.created_at).toLocaleDateString('tr-TR')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(userData.role)}`}>
                        {getRoleDisplayName(userData.role)}
                      </span>
                      <select
                        value={userData.role}
                        onChange={(e) => handleRoleChange(userData.user_id, e.target.value)}
                        className="mobile-input text-sm py-1 px-2 border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="reception">Resepsiyon</option>
                        <option value="warehouse">Depo</option>
                        <option value="admin">Yönetici</option>
                      </select>
                      {userData.user_id !== user.id && (
                        <button
                          onClick={() => handleDeleteUser(userData.user_id, userData.auth?.users?.email)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                          title="Kullanıcıyı Sil"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                      {userData.user_id === user.id && (
                        <span className="p-2 text-gray-400 cursor-not-allowed" title="Kendi hesabınızı silemezsiniz">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Yeni Kullanıcı Ekle</h3>
                <button
                  onClick={() => setShowAddUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="mobile-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Şifre
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="mobile-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="mobile-input"
                  >
                    <option value="reception">Resepsiyon</option>
                    <option value="warehouse">Depo</option>
                    <option value="admin">Yönetici</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddUserModal(false)}
                    className="mobile-button btn-secondary"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="mobile-button btn-primary"
                  >
                    Kullanıcı Ekle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
